import { EventCollectorProps } from '../../../hooks/eventCollector/types';
import { useCallback } from 'react';
import classes from 'classnames';
import SelectEvent from '../../../hooks/eventCollector/events/SelectEvent';
import styles from './Select.module.scss';
import useEventCollector from '../../../hooks/eventCollector/useEventCollector';

export type SelectOption = {
  name: string;
  value: string | number;
};

export type SelectProps = {
  disabled?: boolean;
  value?: string | number;
  name?: string;
  label?: string;
  dataTest?: string;
  options?: SelectOption[];
  error?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  className?: string;
};

export function Select({
  disabled,
  value,
  name,
  label,
  dataTest = 'select',
  options,
  error,
  onChange,
  onBlur,
  className,
  eventCollector,
}: SelectProps & EventCollectorProps) {
  const { track } = useEventCollector();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e);

      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new SelectEvent({
          value: eventCollector?.dontTrackContent
            ? '<not tracked>'
            : eventCollector?.contentOverride ?? e.target.value,
          description: eventCollector?.description || name,
        }),
      );
    },
    [
      eventCollector?.contentOverride,
      eventCollector?.description,
      eventCollector?.dontTrack,
      eventCollector?.dontTrackContent,
      name,
      onChange,
      track,
    ],
  );

  return (
    <div
      className={
        error
          ? classes(
              styles.container,
              styles.field,
              styles['error-background'],
              styles['error-border'],
              styles['error-text'],
              className,
            )
          : classes(styles.container, styles.common, styles.field, className)
      }
    >
      <select
        disabled={disabled}
        name={name}
        data-test={dataTest}
        defaultValue={value || ''}
        value={value}
        onBlur={onBlur}
        onChange={handleChange}
        className={classes(
          styles.select,
          error && styles['error-background'],
          error && styles['error-text'],
        )}
      >
        <option className={styles['hidden-option']} />
        {options?.map(({ name, value }) => (
          <option
            className={classes(styles.option, error && styles['error-text'])}
            key={value}
            value={value}
          >
            {name}
          </option>
        ))}
      </select>

      <label
        className={classes(
          styles.label,
          (value || '').toString().length && styles.float,
        )}
      >
        {label}
      </label>
    </div>
  );
}
export default Select;
